import { firstIncompleteProfileStep, type ProfileUser } from './applicant-form.ts'
import { firstIncompleteCompanyStep, type CompanyUser } from './company-form.ts'
// From `pure.ts`, NOT the `shared.ts` barrel that re-exports it: importing any runtime value from the
// barrel drags its ~376 zod schemas into the chunk, and this module is on the client's login path. See
// the note at the top of pure.ts, and the `shared-barrel/no-barrel-values` lint rule.
import { effectiveCompanyRole } from './pure.ts'
import { safeRelativePath } from './safe-redirect.ts'
import type { EmployeeRole } from './shared.ts'

// The subset of the resolved user the post-login destination depends on. Satisfied by both the
// client's getUser output and the server's `User` (shared/types) — keep it structural so both pass.
export type RedirectUser = ProfileUser &
  CompanyUser & {
    type: 'company' | 'applicant'
    testPassed: boolean | null
    testPassedValues: boolean | null
    testPassedPersonality: boolean | null
    isEmployee?: boolean | null
    // Active-company context (set on the login response), so we can route admin/authorized employees
    // into the company view. `effectiveCompanyRole` reads these two fields.
    activeCompanyId?: number | null
    activeEmployer?: { employeeType?: EmployeeRole | null } | null
  }

export type PostLoginDestination =
  | {
      to:
        | '/report'
        | '/employees'
        | '/register/applicant'
        | '/register/company'
        | '/test/competences'
        | '/test/values'
        | '/test/personality'
    }
  | { href: string }

// Build an onboarding-gate href that threads the visitor's intended post-onboarding target through.
const withRedirect = (path: string, redirect: string): string => `${path}?redirect=${encodeURIComponent(redirect)}`

// Where a user should land right after authenticating. Single source of truth for both the client and the server
// OAuth callback. `redirect` is honored ONLY once the user is fully registered — an unfinished user completes
// onboarding first with the redirect carried along, since honoring it immediately would strand a brand-new user
// on a public, non-test-gated page like /market with no profile to show.
export function resolvePostLoginPath(user: RedirectUser, opts: { redirect?: string } = {}): PostLoginDestination {
  // Sanitized HERE, at the one boundary every consumer shares (the two clients' usePostLoginRedirect and
  // the server's resolveSocialDestination), so no caller can forget it. See safeRelativePath.
  const redirect = safeRelativePath(opts.redirect)

  // An applicant must finish the TEST before any redirect is honored — without it there is no psychographic
  // profile to show. Resume at the first pending questionnaire.
  if (user.type === 'applicant' && (!user.testPassed || !user.testPassedValues || !user.testPassedPersonality)) {
    const to = !user.testPassed ? '/test/competences' : !user.testPassedValues ? '/test/values' : '/test/personality'
    return redirect ? { href: withRedirect(to, redirect) } : { to }
  }

  // A half-registered company must finish its wizard (it has no test of its own) before a redirect is
  // honored, instead of silently landing on Employees (which let companies skip required fields).
  if (user.type === 'company') {
    const incompleteStep = firstIncompleteCompanyStep(user)
    if (incompleteStep)
      return redirect ? { href: withRedirect('/register/company', redirect) } : { to: '/register/company' }
    return redirect ? { href: redirect } : { to: '/employees' }
  }

  // Registered enough (test done): honour an explicit deep-link / compare target (opaque href).
  if (redirect) return { href: redirect }

  // Admin/authorized employees default into the COMPANY view, landing on Employees. Role is null unless an
  // active company context is set, so a personal-email login of an admin still lands on their own profile.
  const role = effectiveCompanyRole(user)
  if (user.isEmployee && (role === 'administrator' || role === 'authorized')) return { to: '/employees' }

  // ── No redirect: an applicant ALWAYS lands on their own profile. A self-registered professional resumes the
  //    profile wizard while it is incomplete; a company-invited employee skips it and lands on the report. Neither
  //    lands on /jobs — that section is for companies and is reached from the nav, never as a default. ──
  const incompleteStep = user.isEmployee ? null : firstIncompleteProfileStep(user)
  return incompleteStep ? { to: '/register/applicant' } : { to: '/report' }
}

// Serialize a destination to a URL string for the server's HTTP redirect (the client uses the navigate
// options directly). `{ href }` is already a built URL; `{ to }` is a bare route path.
export function postLoginHref(dest: PostLoginDestination): string {
  return 'href' in dest ? dest.href : dest.to
}
